// src/components/Footer.jsx
import { Link, useNavigate } from 'react-router-dom'

/* ─── Social links ───────────────────────────────────────────────────────
   Single source of truth for the social row. Only entries with a real `href`
   are rendered — placeholders are omitted entirely rather than shipped as
   dead "#" anchors that take visitors nowhere (client feedback, 5 Aug).

   To bring a channel live, paste the URL into `href` below. Nothing else
   needs to change. */
const SOCIAL_LINKS = [
    {
        name: 'LinkedIn',
        href: null, // TODO: awaiting URL — page created, link not yet supplied
        icon: (
            <>
                <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                <rect x="2" y="9" width="4" height="12" />
                <circle cx="4" cy="4" r="2" />
            </>
        ),
    },
    {
        name: 'X',
        href: null, // TODO: page not yet created
        filled: true,
        icon: <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />,
    },
    {
        name: 'Instagram',
        href: 'https://www.instagram.com/guardianmessagecheck',
        icon: (
            <>
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
            </>
        ),
    },
    {
        name: 'Facebook',
        href: null, // TODO: awaiting URL — page created, link not yet supplied
        icon: <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />,
    },
    {
        name: 'YouTube',
        href: null, // TODO: channel not yet created
        icon: (
            <>
                <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z" />
                <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02" />
            </>
        ),
    },
]

/* Internal link that scrolls to the top of the new page.

   Previously this called window.location.href, which forced a full browser
   reload on every footer link — losing the SPA session and re-downloading the
   bundle. It now navigates through the router and scrolls, so the transition
   is instant. */
const ScrollLink = ({ to, children, className }) => {
    const navigate = useNavigate()

    const handleClick = (e) => {
        if (to.startsWith('/')) {
            e.preventDefault()
            navigate(to)
            window.scrollTo({ top: 0, behavior: 'smooth' })
        }
    }

    return (
        <Link to={to} className={className} onClick={handleClick}>
            {children}
        </Link>
    )
}

// Shared classes for the footer column headings and their links. Raised from
// text-[10px]/text-xs to text-xs/text-sm — client feedback (5 Aug) was that
// the footer lacked presence and read small.
const COL_HEADING =
    'text-xs font-sans font-semibold tracking-widest text-slate-300 uppercase'
const COL_LINK =
    'text-sm font-body font-light text-slate-400 hover:text-white transition-colors duration-200'

export default function Footer() {
    const liveSocials = SOCIAL_LINKS.filter((s) => s.href)

    return (
        <footer className="w-full mt-auto select-none font-sans bg-[#07090e] border-t border-white/[0.04] relative z-20">

            {/* Ambient Base Light Glow inside the Footer Floor */}
            <div className="absolute bottom-0 right-[15%] w-[400px] h-[200px] bg-indigo-500/[0.01] blur-[100px] pointer-events-none rounded-full" />

            {/* Zero Retention — High-End Glassmorphic Feature Banner.
                Given more visual weight: larger padlock, larger heading and
                supporting copy, and a slightly stronger accent wash. This is
                one of GMC's two headline differentiators and should read as
                such rather than as a footnote. */}
            <div className="border-b border-white/[0.04]">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
                    <div className="rounded-2xl bg-white/[0.02] hover:bg-white/[0.035] border border-brand-amber/[0.14] hover:border-brand-amber/[0.24] px-6 sm:px-9 py-8 relative overflow-hidden transition-all duration-300 group">
                        <div className="absolute inset-0 pointer-events-none"
                            style={{ background: 'radial-gradient(ellipse 70% 100% at 0% 0%, rgba(212,168,67,0.07) 0%, transparent 62%)' }} />

                        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                            <div className="flex items-start sm:items-center gap-5">
                                <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-brand-amber/[0.08] border border-brand-amber/25 flex items-center justify-center text-brand-amber shadow-[0_0_24px_-8px_rgba(212,168,67,0.5)]">
                                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
                                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-xl font-display font-semibold text-white tracking-tight mb-2">
                                        Zero Message Retention Guarantee
                                    </p>
                                    <p className="text-sm font-body font-light text-slate-300 leading-relaxed max-w-2xl">
                                        Every message is processed completely in active memory and destroyed on delivery. No logs, no cache, no trail.
                                    </p>
                                </div>
                            </div>

                            <ScrollLink to="/zero-retention"
                                className="flex-shrink-0 inline-flex items-center gap-2 text-sm font-semibold text-brand-amber hover:text-brand-amber2 transition-colors whitespace-nowrap">
                                <span>Learn more</span>
                                <span className="transform group-hover:translate-x-1 transition-transform duration-200">→</span>
                            </ScrollLink>
                        </div>
                    </div>
                </div>
            </div>

            {/* Primary Links Grid */}
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-8 lg:gap-12">

                    {/* Column 0: Brand Block - spans 2 columns on mobile, 1 on desktop */}
                    <div className="col-span-2 md:col-span-1 flex flex-col space-y-4">
                        <ScrollLink to="/" className="text-lg font-display font-bold text-white tracking-tight hover:text-brand-amber transition-colors">
                            GMC<span className="text-brand-amber">.</span>
                        </ScrollLink>
                        <p className="text-sm font-body font-light text-slate-400 leading-relaxed max-w-[260px]">
                            Professional communication risk detection and compliance for the modern workplace.
                        </p>

                        {/* Social row — only live channels are rendered. */}
                        {liveSocials.length > 0 && (
                            <div className="flex items-center gap-4 pt-2 text-slate-400">
                                {liveSocials.map((social) => (
                                    <a
                                        key={social.name}
                                        href={social.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={`GMC on ${social.name}`}
                                        className="hover:text-white transition-colors duration-200"
                                    >
                                        <svg
                                            className="w-5 h-5"
                                            viewBox="0 0 24 24"
                                            fill={social.filled ? 'currentColor' : 'none'}
                                            stroke={social.filled ? 'none' : 'currentColor'}
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            {social.icon}
                                        </svg>
                                    </a>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Column 1: Company Profile */}
                    <div className="flex flex-col space-y-4">
                        <span className={COL_HEADING}>Company</span>
                        <ul className="space-y-3">
                            <li><ScrollLink to="/about" className={COL_LINK}>About</ScrollLink></li>
                            <li><ScrollLink to="/contact" className={COL_LINK}>Contact</ScrollLink></li>
                        </ul>
                    </div>

                    {/* Column 2: Core Actions */}
                    <div className="flex flex-col space-y-4">
                        <span className={COL_HEADING}>Product</span>
                        <ul className="space-y-3">
                            <li><ScrollLink to="/analyze" className={COL_LINK}>Analyse</ScrollLink></li>
                            <li><ScrollLink to="/plans" className={COL_LINK}>Pricing</ScrollLink></li>
                            <li><ScrollLink to="/enterprise" className={COL_LINK}>Enterprise</ScrollLink></li>
                        </ul>
                    </div>

                    {/* Column 3: Insights */}
                    <div className="flex flex-col space-y-4">
                        <span className={COL_HEADING}>Support</span>
                        <ul className="space-y-3">
                            <li><ScrollLink to="/zero-retention" className={COL_LINK}>Zero Retention</ScrollLink></li>
                            <li>
                                <a href="mailto:admin@guardianmessagecheck.com" className={COL_LINK}>
                                    Email Support
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* Column 4: Legal Protocols */}
                    <div className="flex flex-col space-y-4">
                        <span className={COL_HEADING}>Legal</span>
                        <ul className="space-y-3">
                            <li><ScrollLink to="/privacy-policy" className={COL_LINK}>Privacy Policy</ScrollLink></li>
                            <li><ScrollLink to="/terms" className={COL_LINK}>Terms</ScrollLink></li>
                            <li><ScrollLink to="/disclaimer" className={COL_LINK}>Disclaimer</ScrollLink></li>
                        </ul>
                    </div>

                </div>
            </div>

            {/* Bottom bar — copyright and telemetry system indicators.
                Indicators given a little more presence: clearer spacing, a
                shield glyph on the compliance badge and a slightly stronger
                type size, so they read as genuine platform status rather than
                decorative text. */}
            <div className="border-t border-white/[0.04] bg-white/[0.005]">

                {/* Structural disclaimer — required on every page without
                    exception (brief v1.1, "Footer Disclaimer"). Wording is
                    the client's and must not be paraphrased. */}
                <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-7">
                    <p className="text-xs font-body font-light text-slate-500 leading-relaxed max-w-4xl">
                        GMC provides AI-powered communication guidance only. It does not constitute
                        legal, HR, or professional advice. Users are solely responsible for the
                        content and consequences of messages they choose to send.
                    </p>
                </div>

                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-7 flex flex-col sm:flex-row items-center justify-between gap-5">

                    <span className="text-sm font-body font-light text-slate-400">
                        © {new Date().getFullYear()} Guardian Message Check. All rights reserved.
                    </span>

                    <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs font-sans text-slate-400">
                        <div className="flex items-center gap-2 text-emerald-400">
                            <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_#10b981]" />
                            <span className="font-medium tracking-wide">System Online</span>
                        </div>

                        <span className="text-white/[0.08]">|</span>

                        <div className="flex items-center gap-2 text-slate-300">
                            <svg className="w-4 h-4 text-brand-amber" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                            <span className="font-medium tracking-wide">AES-256</span>
                        </div>

                        <span className="text-white/[0.08]">|</span>

                        <div className="flex items-center gap-2 text-slate-300">
                            <svg className="w-4 h-4 text-brand-amber" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                            </svg>
                            <span className="font-medium tracking-wide">GDPR Compliant</span>
                        </div>
                    </div>

                </div>
            </div>
        </footer>
    )
}